// Ongoing (not one-time) commercial ability profile and training
// recommendations. The Diagnostic's own scoring (commercialDiagnosticScoring.js)
// is a frozen snapshot of a single run; this blends in every commercial-module
// practice attempt as it happens, via cbatScoring.js's already-generic
// buildModuleScoreMap, so the dashboard keeps reflecting real progress
// instead of going stale the moment a user starts practicing individual
// modules after their diagnostic. Falls back to the diagnostic's own
// per-domain evidence only where a domain has no live practice yet, so a
// fresh diagnostic still has immediate value on day one.
import { buildModuleScoreMap } from './cbatScoring';
import { MODULES } from '../constants/modules';
import { DIAGNOSTIC_DOMAINS, ALL_COMMERCIAL_MODULE_DOMAINS } from '../data/commercialDiagnosticProfile';
import { aggregateDiagnosticDomainScores } from './commercialDiagnosticScoring';

export const COMMERCIAL_MODULES = MODULES.filter((module) => module.preparationFamily === 'commercial');

const round1 = (value) => Math.round(value * 10) / 10;

const modulesInDomain = (domain) => COMMERCIAL_MODULES.filter((module) => ALL_COMMERCIAL_MODULE_DOMAINS[module.id] === domain);

// domain score stays on the same 0-100 accuracy-like scale
// aggregateDiagnosticDomainScores already uses (plan.txt section 74's
// example table: "Numerical 72", "Spatial 79" etc.) so the panel reads
// consistently whether a domain's value came from the diagnostic snapshot
// or from live module practice.
export const buildLiveCommercialAbilityProfile = (history, diagnosticRun = null) => {
  const moduleScores = buildModuleScoreMap(history || []);
  const diagnosticByDomain = new Map(
    (diagnosticRun ? aggregateDiagnosticDomainScores(diagnosticRun) : []).map((entry) => [entry.domain, entry])
  );

  return DIAGNOSTIC_DOMAINS.map((domain) => {
    const liveScores = modulesInDomain(domain)
      .map((module) => moduleScores[module.id])
      .filter(Boolean);

    if (liveScores.length) {
      const accuracy = liveScores.reduce((sum, score) => sum + score.accuracy, 0) / liveScores.length;
      return {
        domain,
        score: round1(accuracy),
        hasEvidence: true,
        source: 'live',
        moduleCount: liveScores.length
      };
    }

    const diagnosticEntry = diagnosticByDomain.get(domain);
    if (diagnosticEntry?.hasEvidence) {
      return { domain, score: diagnosticEntry.score, hasEvidence: true, source: 'diagnostic', moduleCount: 0 };
    }

    return { domain, score: null, hasEvidence: false, source: null, moduleCount: 0 };
  });
};

const NEVER_ATTEMPTED_REASON = 'Not practiced yet';
const LOW_SCORE_STANINE_CEILING = 4;

// Module-level (not domain-level) recommendations: a concrete list of
// specific modules worth practicing next, mirroring the shape of the
// military trainingRecommendations.js output (moduleId/module/priority/
// reason) but driven by raw commercial module coverage and accuracy
// rather than service-profile role requirements, since no such
// requirement table exists (or should be invented, per plan.txt's "Do NOT
// invent" list) for any commercial employer.
export const buildCommercialTrainingRecommendations = (history, { limit = 5 } = {}) => {
  const moduleScores = buildModuleScoreMap(history || []);

  const neverAttempted = COMMERCIAL_MODULES
    .filter((module) => !moduleScores[module.id])
    .map((module) => ({
      moduleId: module.id,
      module,
      priority: 'high',
      reason: NEVER_ATTEMPTED_REASON
    }));

  const lowScoring = COMMERCIAL_MODULES
    .filter((module) => moduleScores[module.id] && moduleScores[module.id].stanine <= LOW_SCORE_STANINE_CEILING)
    .map((module) => {
      const score = moduleScores[module.id];
      return {
        moduleId: module.id,
        module,
        priority: score.stanine <= 2 ? 'high' : 'medium',
        reason: `Academy estimate: stanine ${score.stanine} of 9`,
        stanine: score.stanine
      };
    })
    .sort((a, b) => a.stanine - b.stanine);

  const priorityRank = { high: 0, medium: 1, low: 2 };
  return [...neverAttempted, ...lowScoring]
    .sort((a, b) => priorityRank[a.priority] - priorityRank[b.priority])
    .slice(0, limit);
};
